import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ImageGallery from './components/ImageGallery';
import ProductInfo from './components/ProductInfo';
import SpecificationsPanel from './components/SpecificationsPanel';
import UsageInstructions from './components/UsageInstructions';
import ReviewsSection from './components/ReviewsSection';
import RelatedProducts from './components/RelatedProducts';
import TrustSignals from './components/TrustSignals';
import Icon from '../../components/AppIcon';

const ProductDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');

  const productData = {
    id: 1,
    name: "BPC-157 Premium 5mg",
    shortDescription: "Advanced healing peptide for tissue repair, gut health, and accelerated recovery from injuries and intense training sessions.",
    category: "Recovery & Healing",
    price: 49.99,
    originalPrice: 69.99,
    inStock: true,
    stockCount: 47,
    dosage: "5mg per vial",
    purity: "99.9% pharmaceutical grade",
    storage: "Store at -20°C (freezer)",
    maxQuantity: 10,
    isNew: true,
    images: [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_105ce3fbf-1766470445165.png",
      alt: "Close-up view of BPC-157 peptide vial with blue cap on white laboratory surface with scientific equipment in background"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_12d63bcd9-1764699716363.png",
      alt: "Multiple BPC-157 peptide vials arranged in row showing product packaging and pharmaceutical labeling on sterile white surface"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_105ce3fbf-1766470445165.png",
      alt: "Laboratory technician in white coat holding BPC-157 vial up to light for quality inspection in modern research facility"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_12d63bcd9-1764699716363.png",
      alt: "BPC-157 peptide powder in glass container on precision scale showing exact measurement in professional laboratory setting"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_109bfc5b4-1766470445169.png",
      alt: "Sterile syringe and BPC-157 vial on medical tray with alcohol swabs and safety equipment for proper administration"
    }],

    specifications: [
    { icon: "Atom", label: "Molecular Formula", value: "C62H98N16O22" },
    { icon: "Weight", label: "Molecular Weight", value: "1419.53 g/mol" },
    { icon: "Droplet", label: "Solubility", value: "Water soluble" },
    { icon: "Thermometer", label: "Melting Point", value: "Not applicable (peptide)" },
    { icon: "Beaker", label: "pH Range", value: "5.0 - 7.0" },
    { icon: "Package", label: "Package Contents", value: "1 vial + bacteriostatic water" },
    { icon: "Calendar", label: "Shelf Life", value: "24 months (unopened)" },
    { icon: "Shield", label: "Certification", value: "GMP certified facility" }],

    instructions: [
    {
      title: "Reconstitution",
      description: "Add 2ml of bacteriostatic water slowly down the side of the vial. Gently swirl (do not shake) until powder is completely dissolved. Allow to sit for 5 minutes before use.",
      warning: "Never shake the vial vigorously as this can damage the peptide structure."
    },
    {
      title: "Dosage Guidelines",
      description: "Typical dosage ranges from 250mcg to 500mcg per injection, administered once or twice daily. Start with lower doses and gradually increase based on individual response and goals.",
      warning: "Do not exceed 1000mcg per day without medical supervision."
    },
    {
      title: "Administration Method",
      description: "Administer via subcutaneous injection in areas with adequate subcutaneous fat such as abdomen, thigh, or upper arm. Rotate injection sites to prevent tissue irritation.",
      warning: "Always use sterile technique and new needles for each injection."
    },
    {
      title: "Storage After Reconstitution",
      description: "Once reconstituted, store in refrigerator at 2-8°C (36-46°F). Use within 30 days for optimal potency. Keep away from light and do not freeze reconstituted solution."
    }],

    description: `BPC-157 (Body Protection Compound-157) is a synthetic peptide derived from a protective protein found in the stomach. This remarkable compound has gained significant attention in the research community for its potential therapeutic applications in tissue repair and regeneration.\n\nKey Benefits:\n• Accelerates healing of muscles, tendons, and ligaments\n• Supports gut health and digestive function\n• Promotes angiogenesis (formation of new blood vessels)\n• Reduces inflammation and oxidative stress\n• May improve joint health and mobility\n• Supports recovery from injuries and surgical procedures\n\nResearch Applications:\nBPC-157 has been extensively studied for its regenerative properties. Research indicates potential benefits for treating various conditions including tendon injuries, muscle tears, inflammatory bowel disease, and wound healing. The peptide works by promoting the formation of new blood vessels and supporting the body's natural healing processes.\n\nQuality Assurance:\nOur BPC-157 is manufactured in a GMP-certified facility and undergoes rigorous third-party testing to ensure 99.9% purity. Each batch is accompanied by a Certificate of Analysis (COA) verifying its authenticity and quality. We use only pharmaceutical-grade ingredients and follow strict quality control protocols throughout the production process.`
  };

  const reviews = [
  {
    id: 1,
    name: "Marcus Thompson",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa9e44fc-1763295892954.png",
    avatarAlt: "Professional headshot of athletic African American man with short fade haircut wearing navy blue athletic shirt",
    rating: 5,
    date: "2026-01-28",
    verified: true,
    comment: "Absolutely incredible results! I've been using BPC-157 for a nagging shoulder injury that wasn't healing properly. Within two weeks, I noticed significant improvement in pain levels and range of motion. By week four, I was back to full training capacity. The quality is outstanding and the shipping was fast. Highly recommend!",
    helpful: 24,
    notHelpful: 2
  },
  {
    id: 2,
    name: "Sarah Chen",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f1ff0d66-1763296654754.png",
    avatarAlt: "Professional headshot of Asian woman with long black hair wearing white lab coat in medical setting",
    rating: 5,
    date: "2026-01-15",
    verified: true,
    comment: "As a healthcare professional, I'm very particular about supplement quality. This BPC-157 exceeded my expectations. The purity is evident, and the COA documentation is thorough. I've been using it for gut health support and have noticed remarkable improvements in digestive comfort and overall wellness.",
    helpful: 18,
    notHelpful: 1
  },
  {
    id: 3,
    name: "David Rodriguez",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1727ab32b-1763291738707.png",
    avatarAlt: "Professional headshot of Hispanic man with short brown hair and beard wearing gray athletic wear",
    rating: 4,
    date: "2026-01-05",
    verified: true,
    comment: "Great product overall. Helped significantly with my tennis elbow that had been bothering me for months. Only giving 4 stars because the price is a bit high, but the quality justifies it. Would purchase again.",
    helpful: 12,
    notHelpful: 3
  }];


  const relatedProducts = [
  {
    id: 2,
    name: "TB-500 Premium 5mg",
    description: "Powerful healing peptide for injury recovery and tissue repair",
    category: "Recovery & Healing",
    price: 59.99,
    rating: 4.8,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14de434ff-1766470445027.png",
    imageAlt: "TB-500 peptide vial with green cap on laboratory surface with medical equipment and sterile packaging"
  },
  {
    id: 3,
    name: "Ipamorelin 5mg",
    description: "Growth hormone secretagogue for muscle growth and fat loss",
    category: "Performance Enhancement",
    price: 44.99,
    rating: 4.9,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_105ce3fbf-1766470445165.png",
    imageAlt: "Ipamorelin peptide vial with red cap displayed on white surface with precision measurement tools"
  },
  {
    id: 4,
    name: "CJC-1295 DAC 2mg",
    description: "Long-acting growth hormone releasing hormone for enhanced recovery",
    category: "Performance Enhancement",
    price: 54.99,
    rating: 4.7,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_126322844-1766470447168.png",
    imageAlt: "CJC-1295 peptide vial with purple cap on sterile medical tray with syringes and alcohol swabs"
  }];


  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location?.pathname]);

  const handleAddToCart = () => {
    console.log(`Added ${quantity} units of ${productData?.name} to cart`);
    alert(`Successfully added ${quantity} unit(s) to your cart!`);
  };

  const tabs = [
  { id: 'description', label: 'Description', icon: 'FileText' },
  { id: 'specifications', label: 'Specifications', icon: 'Settings' },
  { id: 'usage', label: 'Usage Instructions', icon: 'BookOpen' },
  { id: 'reviews', label: 'Reviews', icon: 'MessageSquare' }];


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-12 px-4 md:px-6 lg:px-8">
        <div className="max-w-[1440px] mx-auto">
          <button
            onClick={() => navigate('/product-catalog')}
            className="flex items-center gap-2 mb-6 px-4 py-2 rounded-lg bg-card border border-border text-foreground transition-all duration-250 hover:bg-primary/10 hover:border-primary">
            
            <Icon name="ArrowLeft" size={18} color="var(--color-primary)" />
            <span className="font-medium">Back to Products</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
            <ImageGallery images={productData?.images} productName={productData?.name} />
            <ProductInfo
              product={productData}
              quantity={quantity}
              onQuantityChange={setQuantity}
              onAddToCart={handleAddToCart} />
            
          </div>

          <div className="mb-12">
            <TrustSignals />
          </div>

          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-250 whitespace-nowrap flex-shrink-0 ${
                activeTab === tab?.id ?
                'bg-primary text-primary-foreground shadow-lg shadow-primary/30' :
                'bg-card border border-border text-foreground hover:bg-primary/10 hover:border-primary'}`
                }>
                
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              )}
            </div>

            {activeTab === 'description' &&
            <div className="p-6 md:p-8 rounded-xl bg-card border border-border">
                <div className="prose prose-invert max-w-none">
                  {productData?.description?.split('\n')?.map((paragraph, index) =>
                <p key={index} className="text-base text-foreground leading-relaxed mb-4">
                      {paragraph}
                    </p>
                )}
                </div>
              </div>
            }

            {activeTab === 'specifications' &&
            <SpecificationsPanel specifications={productData?.specifications} />
            }

            {activeTab === 'usage' &&
            <UsageInstructions instructions={productData?.instructions} />
            }

            {activeTab === 'reviews' &&
            <ReviewsSection
              reviews={reviews}
              averageRating={4.8}
              totalReviews={reviews?.length} />

            }
          </div>

          <RelatedProducts products={relatedProducts} />
        </div>
      </main>
      <footer className="bg-card border-t border-border py-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-[1440px] mx-auto text-center">
          <p className="text-muted-foreground caption">
            © {new Date()?.getFullYear()} PeptideHub. All rights reserved.
          </p>
        </div>
      </footer>
    </div>);

};

export default ProductDetailPage;